import {FreightTypeEntity, getObjectId, RateModelEntity, ServiceTypeEntity} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps, NumberInput} from "@mantine/core";
import {useCallback} from "react";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {ServiceTypeSelect} from "../../select/entity/ServiceTypeSelect/ServiceTypeSelect";
import {RateModelSelect} from "../../select/entity/RateModelSelect/RateModelSelect";
import {InvoiceGroupBySelect} from "../../select/entity/InvoiceGroupBySelect/InvoiceGroupBySelect";
import {InvoiceGroupByTypeSelect} from "../../select/entity/InvoiceGroupByTypeSelect/InvoiceGroupByTypeSelect";

interface InvoiceGroupingDefaultFormProps {
  options?: InvoiceGroupingDefaultFormOptions;
  defaultValues?: Partial<InvoiceGroupingDefaultFormValues>;
  onSubmit?: (values: InvoiceGroupingDefaultFormValues) => void;
  submitting?: boolean;
}

export interface InvoiceGroupingDefaultFormOptions {
  // props here
  regionId?: string
}

export interface InvoiceGroupingDefaultFormValues {
  rateModel: RateModelEntity | string
  type: string
  invoiceGroupBy: string
  invoiceMaxCost?: number
  invoiceMaxLineItem?: number
  freightType?: FreightTypeEntity | string;
  serviceType?: ServiceTypeEntity | string
}

const getDefaultValues = (defaultValues: Partial<InvoiceGroupingDefaultFormValues> | undefined) => {
  return {
    rateModel: getObjectId(defaultValues?.rateModel),
    type: defaultValues?.type,
    invoiceGroupBy: defaultValues?.invoiceGroupBy,
    invoiceMaxCost: defaultValues?.invoiceMaxCost,
    invoiceMaxLineItem: defaultValues?.invoiceMaxLineItem,
    freightType: getObjectId(defaultValues?.freightType),
    serviceType: getObjectId(defaultValues?.serviceType)
  }
}

export const InvoiceGroupingDefaultForm = (props: InvoiceGroupingDefaultFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        rateModel: values.rateModel!,
        type: values.type!,
        invoiceGroupBy: values.invoiceGroupBy!,
        invoiceMaxCost: values.invoiceMaxCost,
        invoiceMaxLineItem: values.invoiceMaxLineItem,
        freightType: values.freightType,
        serviceType: values.serviceType,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <RateModelSelect
        label={t("common.entity.invoicegroupingdefault.form.field.ratemodel", "Rate Model")}
        placeholder={t<string>("common.entity.invoicegroupingdefault.form.field.ratemodel", "Rate Model")}
        styles={{wrapper: {width: "100%"}}}
        selector={{
          regionId: options?.regionId,
        }}
        withAsterisk
        required
        clearable
        {...form.getInputProps("rateModel")} />
      <Divider my="sm" variant="dotted"/>
      <InvoiceGroupByTypeSelect
        label={t("common.entity.invoicegroupingdefault.form.field.type", "Type")}
        placeholder={t<string>("common.entity.invoicegroupingdefault.form.field.type", "Type")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("type")} />
      <InvoiceGroupBySelect
        label={t("common.entity.invoicegroupingdefault.form.field.invoicegroupby", "Group By")}
        placeholder={t<string>("common.entity.invoicegroupingdefault.form.field.invoicegroupby", "Group By")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("invoiceGroupBy")} />
      <Divider my="sm" variant="dotted"/>
      <NumberInput
        label={t("common.entity.invoicegroupingdefault.form.field.invoicemaxcost", "Max Cost")}
        placeholder={t<string>("common.entity.invoicegroupingdefault.form.field.invoicemaxcost", "Max Cost")}
        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
        formatter={(value) =>
          !Number.isNaN(parseFloat(value))
            ? `$ ${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
            : '$ '
        }
        precision={5}
        {...form.getInputProps("invoiceMaxCost")} />
      <NumberInput
        label={t("common.entity.invoicegroupingdefault.form.field.invoicemaxlineitem", "Max Line Items")}
        placeholder={t<string>("common.entity.invoicegroupingdefault.form.field.invoicemaxlineitem", "Max Line Items")}
        {...form.getInputProps("invoiceMaxLineItem")} />
      <Divider my="sm" variant="dotted"/>
      <FreightTypeSelect
        label={t("common.entity.invoicegroupingdefault.form.field.freighttype", "Freight Type")}
        placeholder={t<string>("common.entity.invoicegroupingdefault.form.field.freighttype.placeholder", "Freight Type")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        {...form.getInputProps("freightType")} />
      <ServiceTypeSelect
        label={t("common.entity.invoicegroupingdefault.form.field.servicetype", "Service Type")}
        placeholder={t<string>("common.entity.invoicegroupingdefault.form.field.servicetype.placeholder", "Service Type")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        {...form.getInputProps("serviceType")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface InvoiceGroupingDefaultFormModalProps extends InvoiceGroupingDefaultFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const InvoiceGroupingDefaultFormModal = (props: InvoiceGroupingDefaultFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    options,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <InvoiceGroupingDefaultForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        submitting={submitting}
        options={options}/>
    </Modal>
  );
};
